import { Box, MenuItem, Select, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { formatCurrency } from "../../../utils";

type WashAccount = {
  key: string;
  numberOfWashes: number;
};

type InvoiceAccount = {
  key: string;
  invoiceAmount: number;
  numberOfInvoices: number;
};

export type TopPerformingTenantsTableProps = {
  washes: WashAccount[];
  invoices: InvoiceAccount[];
};

type SortBy = "washes" | "invoice" | "spent";

type MobileMetricProps = {
  label: string;
  value: string | number;
  valueColor?: string;
};

const MobileMetric = ({ label, value, valueColor }: MobileMetricProps) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography
      sx={{
        color: "#94A3B8",
        fontSize: 11,
        fontWeight: 700,
        textTransform: "uppercase",
      }}
    >
      {label}
    </Typography>

    <Typography
      sx={{
        color: valueColor || "#0F172A",
        fontSize: 13,
        fontWeight: 700,
        overflowWrap: "anywhere",
      }}
    >
      {value}
    </Typography>
  </Box>
);

export const TopPerformingTenantsTable = ({
  washes,
  invoices,
}: TopPerformingTenantsTableProps) => {
  const [sortBy, setSortBy] = useState<SortBy>("washes");

  const invoiceMap = useMemo(() => {
    return new Map(invoices.map((invoice) => [invoice.key, invoice]));
  }, [invoices]);

  const rows = useMemo(() => {
    const invoiceOnlyKeys = invoices
      .map((invoice) => invoice.key)
      .filter((key) => !washes.some((wash) => wash.key === key));

    const washRows = washes.map((wash) => {
      const invoice = invoiceMap.get(wash.key);

      return {
        tenantName: wash.key,
        totalWashes: wash.numberOfWashes,
        totalSpent: invoice?.invoiceAmount ?? 0,
        avgInvoice: invoice?.numberOfInvoices
          ? invoice.invoiceAmount / invoice.numberOfInvoices
          : 0,
      };
    });
    const invoiceOnlyRows = invoiceOnlyKeys.map((key) => {
      const invoice = invoiceMap.get(key)!;

      return {
        tenantName: key,
        totalWashes: 0,
        totalSpent: invoice.invoiceAmount,
        avgInvoice: invoice.numberOfInvoices
          ? invoice.invoiceAmount / invoice.numberOfInvoices
          : 0,
      };
    });

    const merged = [...washRows, ...invoiceOnlyRows];

    switch (sortBy) {
      case "invoice":
        merged.sort((a, b) => b.avgInvoice - a.avgInvoice);
        break;

      case "spent":
        merged.sort((a, b) => b.totalSpent - a.totalSpent);
        break;

      default:
        merged.sort((a, b) => b.totalWashes - a.totalWashes);
    }

    return merged.slice(0, 10);
  }, [invoiceMap, washes, sortBy]);

  return (
    <Box
      sx={{
        mt: 2,
        borderRadius: "18px",
        backgroundColor: "#fff",
        boxShadow: "0px 2px 12px rgba(0,0,0,.08)",
        overflow: "hidden",
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          p: { xs: 2, sm: 3 },
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: { xs: 18, sm: 22 },
              fontWeight: 700,
            }}
          >
            Top Performing Customer Accounts
          </Typography>

          <Typography
            sx={{
              fontSize: 14,
              color: "#94A3B8",
            }}
          >
            Tiered based on selected activity
          </Typography>
        </Box>

        <Select
          size="small"
          value={sortBy}
          sx={{
            width: { xs: "100%", sm: "auto" },
          }}
          onChange={(e) => setSortBy(e.target.value as SortBy)}
        >
          <MenuItem value="washes">Total Washes</MenuItem>

          <MenuItem value="invoice">Avg Invoice</MenuItem>

          <MenuItem value="spent">Total Spent</MenuItem>
        </Select>
      </Box>

      <Box
        sx={{
          display: { xs: "none", sm: "grid" },
          gridTemplateColumns: "3fr 1fr 1fr 1fr",
          px: 3,
          py: 2,
          backgroundColor: "#F8FAFC",
          borderTop: "1px solid #E5E7EB",
          borderBottom: "1px solid #E5E7EB",
          fontWeight: 700,
          color: "#94A3B8",
          fontSize: 12,
          textTransform: "uppercase",
          letterSpacing: 1,
        }}
      >
        <Typography>Tenant Name</Typography>
        <Typography>Total Washes</Typography>
        <Typography>Avg Invoice</Typography>
        <Typography>Total Spent</Typography>
      </Box>

      {rows.map((row) => (
        <Box
          key={row.tenantName}
          sx={{
            display: { xs: "block", sm: "grid" },
            gridTemplateColumns: {
              sm: "3fr 1fr 1fr 1fr",
            },
            px: { xs: 2, sm: 3 },
            py: { xs: 2, sm: 2.5 },
            borderBottom: "1px solid #F1F5F9",
            alignItems: "center",
          }}
        >
          <Typography
            fontWeight={600}
            sx={{
              overflowWrap: "anywhere",
            }}
          >
            {row.tenantName}
          </Typography>

          <Typography
            sx={{
              display: { xs: "none", sm: "block" },
            }}
          >
            {row.totalWashes}
          </Typography>

          <Typography
            sx={{
              display: { xs: "none", sm: "block" },
            }}
          >
            {formatCurrency(row.avgInvoice)}
          </Typography>

          <Typography
            sx={{
              color: "#10B981",
              fontWeight: 700,
              display: { xs: "none", sm: "block" },
            }}
          >
            {formatCurrency(row.totalSpent)}
          </Typography>

          <Box
            sx={{
              display: { xs: "grid", sm: "none" },
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              gap: 1,
              mt: 1.5,
            }}
          >
            <MobileMetric label="Washes" value={row.totalWashes} />

            <MobileMetric
              label="Avg Invoice"
              value={formatCurrency(row.avgInvoice)}
            />

            <MobileMetric
              label="Total Spent"
              value={formatCurrency(row.totalSpent)}
              valueColor="#10B981"
            />
          </Box>
        </Box>
      ))}
    </Box>
  );
};
